// markers/pdar.ts

export type PdarMarkerType = "platform" | "target" | "sensor";
export type OperationalState = "nominal" | "warning" | "critical" | "offline";
export type Intent = "friendly" | "hostile" | "neutral" | "unknown";

// 🎯 Normalized baseline multipliers tailored to your layout layout density rules.
const sizeScale: Record<PdarMarkerType, number> = {
  platform: 0.75,
  target: 0.9,
  sensor: 0.9,
};

// 🎨 Clean, high-visibility tactical palette
const INTENT_COLORS: Record<Intent, string> = {
  friendly: "#00E5FF", // Tactical Cyan
  hostile: "#dc2626", // Alarm Red
  neutral: "#00FF66", // Stable Green
  unknown: "#FFFF33", // Caution Yellow
};

const STATE_COLORS: Record<OperationalState, string> = {
  nominal: "#65a30d", // High-Vis Tactical Green
  warning: "#FFA500", // Amber
  critical: "#FF3333", // Solid Red
  offline: "#757575", // Muted Grey
};

const pdarIcons: Record<
  PdarMarkerType,
  (color: string, scale: number) => string
> = {
  // 🛳️ Mother Ship/Vessel Platform Vector - High-Res 256x256 Output Frame
  platform: (color, s) => {
    const path = `
    <g transform="translate(558.674 1546.322)">
      <g transform="matrix(0.719, -0.695, 0.695, 0.719, 0, 75.643)">
        <path d="M108.893,0C39.042,3.663,0,50.422,0,50.422H22.661L59.827,85.893v24.922S102.628,85.5,108.893,0"/>
      </g>
      <path d="M0,45.527V33.759l4.484-5.089V5.2L9.791,0l5.067,5.2V28.671h9.264V5.2L29.564,0l4.828,5.2V28.671L39.3,33.759V45.527Z" transform="translate(58.004 37.938)"/>
      <path d="M0,45.527V33.759l4.484-5.089V5.2L9.791,0l5.067,5.2V28.671h9.264V5.2L29.564,0l4.828,5.2V28.671L39.3,33.759V45.527Z" transform="matrix(-1, -0.017, 0.017, -1, 96.657, 230.844)"/>
      <g transform="translate(34.968 155.357)">
        <rect width="85.37" height="67.415" rx="9.5" transform="translate(0 23.65)"/>
        <path d="M9383.7-1929.34h84.571v-42.16l-16.33,23.658h-51.075L9382.99-1971.5v43.134" transform="translate(-9382.953 1971.5)"/>
        <path d="M0,45.527V33.759l4.484-5.089V5.2L9.791,0l5.067,5.2V28.671h9.264V5.2L29.564,0l4.828,5.2V28.671L39.3,33.759V45.527Z" transform="translate(63.139 78.44) rotate(-180)"/>
      </g>
      <rect width="28.385" height="39.03" rx="3" transform="translate(63.215 122.991)"/>
    </g>`;

    return `
    <svg xmlns="http://www.w3.org/2000/svg" width="256" height="256" viewBox="0 0 256 256">
      <g transform="translate(128 128) scale(${s}) translate(-128 -128)" fill="none" stroke-linecap="round" stroke-linejoin="round">
        <g stroke="#101012" stroke-width="14.0" transform="translate(-509 -1541)">${path}</g>
        <g stroke="${color}" stroke-width="6.0" transform="translate(-509 -1541)">${path}</g>
      </g>
    </svg>`;
  },

  // 🎯 Drone Intercept Target Vector - Tabler Blueprint Core at 128x128 Output Frame
  target: (color, s) => {
    const path = `
    <path d="M106.667 106.667h42.666v42.666h-42.666z"/>
    <path d="M106.667 106.667L69.333 69.333"/>
    <path d="M106.24 64a37.333 37.333 0 1 0 -42.24 42.24"/>
    <path d="M149.333 106.667l37.334 -37.334"/>
    <path d="M192 106.24a37.333 37.333 0 1 0 -42.24 -42.24"/>
    <path d="M149.333 149.333l37.334 37.334"/>
    <path d="M149.76 192a37.333 37.333 0 1 0 42.24 -42.24"/>
    <path d="M106.667 149.333L69.333 186.667"/>
    <path d="M64 149.76a37.333 37.333 0 1 0 42.24 42.24"/>`;

    return `
    <svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 256 256">
      <g transform="translate(128 128) scale(${s}) translate(-128 -128)" fill="none" stroke-linecap="round" stroke-linejoin="round">
        <g stroke="#101012" stroke-width="18.0">${path}</g>
        <g stroke="${color}" stroke-width="8.5">${path}</g>
      </g>
    </svg>`;
  },

  // 📡 Radar/Sensor Tracking Vector - Tabler Blueprint Core at 128x128 Output Frame
  sensor: (color, s) => {
    const path = `
    <path d="M224 128h-85.333a10.667 10.667 0 1 0 -10.667 10.667v85.333a96 96 0 0 0 96 -96"/>
    <path d="M170.667 96a53.333 53.333 0 1 0 -74.667 74.667"/>
    <path d="M218.517 96a96 96 0 1 0 -122.474 122.613"/>`;

    return `
    <svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 256 256">
      <g transform="translate(128 128) scale(${s}) translate(-128 -128)" fill="none" stroke-linecap="round" stroke-linejoin="round">
        <g stroke="#101012" stroke-width="18.0">${path}</g>
        <g stroke="${color}" stroke-width="8.5">${path}</g>
      </g>
    </svg>`;
  },
};

interface MarkerOptions {
  type?: string;
  isSelected?: boolean;
  intent?: Intent;
  state?: OperationalState;
}

export function createPdarMarker(options: MarkerOptions = {}): string {
  const type = (options.type || "sensor")
    .toLowerCase()
    .trim() as PdarMarkerType;
  const sanitizedKey = pdarIcons[type] ? type : "sensor";

  const scale = sizeScale[sanitizedKey];
  const iconTemplate = pdarIcons[sanitizedKey];

  let color = "#F5F5F5"; // Default White fallback

  if (options.isSelected) {
    // Selection state always overrides with Champagne highlight
    color = "#FFEBB3";
  } else if (sanitizedKey === "platform") {
    // Platforms always remain tactical white
    color = "#F5F5F5";
  } else if (sanitizedKey === "target") {
    // Targets strictly use INTENT_COLORS mapping
    color = INTENT_COLORS[options.intent || "neutral"] || "#F5F5F5";
  } else if (sanitizedKey === "sensor") {
    // 🛡️ Map incoming tracking intent contexts safely to states if state is omitted
    // This allows sprite register loops to build discrete friendly/hostile textures cleanly.
    let mappedState = options.state;
    if (!mappedState && options.intent) {
      if (options.intent === "hostile") {
        mappedState = "critical";
      } else if (options.intent === "neutral") {
        mappedState = "warning";
      } else {
        mappedState = "nominal";
      }
    }

    // Sensors strictly use STATE_COLORS mapping
    color = STATE_COLORS[mappedState || "nominal"] || "#65a30d";
  }

  return iconTemplate(color, scale);
}
